import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ToastContext from '../../../../../context/ToastContext';
import useAxios from '../../../../../hooks/useAxios';
import usePrevious from '../../../../../hooks/usePrevious';
import { ImportListCollection } from '../../../../../types/api/importlists';
import { Unpacked } from '../../../../../types/util';
import {
  errorToast,
  successToast,
} from '../../../../../utils/helpers/primereact';

type Props = {
  selectedList: Unpacked<ImportListCollection> | undefined;
  isShown: boolean;
  onHide: () => void;
  reloadImportedLists: () => void;
};

function DeleteDialog({
  selectedList,
  isShown,
  onHide,
  reloadImportedLists,
}: Props) {
  const { t } = useTranslation();

  const { toastRef } = useContext(ToastContext);
  const {
    data,
    error,
    reload: submissionRequest,
    isLoading: isSubmissionRequestLoading,
  } = useAxios();

  const dataPrevious = usePrevious(data);
  const errorPrevious = usePrevious(error);

  useEffect(() => {
    if (!data || dataPrevious === data || !toastRef?.current) {
      return;
    }

    reloadImportedLists();
    onHide();

    successToast(
      toastRef,
      t('Success'),
      t('Imported list {{selectedListName}} has been successfully deleted', {
        selectedListName: selectedList?.name,
      })
    );
  }, [
    data,
    dataPrevious,
    onHide,
    reloadImportedLists,
    selectedList?.name,
    t,
    toastRef,
  ]);

  useEffect(() => {
    if (!error || error === errorPrevious || !toastRef?.current) {
      return;
    }

    onHide();

    errorToast(
      toastRef,
      t('Error'),
      t(
        'An error occured while trying to delete the import list {{selectedListName}}. Please try again later or contact the Koder team.',
        { selectedListName: selectedList?.name }
      )
    );
  }, [error, errorPrevious, onHide, selectedList?.name, t, toastRef]);

  function handleDeleteSubmision(selectedList: any) {
    submissionRequest({
      url: `/orders/bulk/${selectedList?.id}`,
      method: 'DELETE',
    });
  }

  const dialogHeader = <span>{t('Are you sure?')}</span>;

  const dialogFooter = (
    <>
      <Button
        type="button"
        label={t('Cancel')}
        onClick={onHide}
        className="p-button-outlined"
      />
      <Button
        type="button"
        label={isSubmissionRequestLoading ? t('Deleting...') : t('Delete')}
        disabled={isSubmissionRequestLoading}
        onClick={() => handleDeleteSubmision(selectedList)}
        className="p-button-danger"
        data-cy="submit-btn"
      />
    </>
  );

  return (
    <Dialog
      visible={isShown}
      onHide={onHide}
      header={dialogHeader}
      footer={dialogFooter}
      dismissableMask
      resizable={false}
    >
      {t(
        'Are you sure you want to delete {{selectedListName}} imported list?',
        { selectedListName: selectedList?.name }
      )}
    </Dialog>
  );
}

export default DeleteDialog;
